import React, { useState, useEffect } from 'react';

const ProcessTree = ({ project, selectedProcess, onSelectProcess }) => {
  const [expandedCategories, setExpandedCategories] = useState({});
  const [processTreeExpanded, setProcessTreeExpanded] = useState(true);

  // Auto-expand all categories on project load
  useEffect(() => {
    if (project && project.processes) {
      const cats = {};
      Object.values(project.processes).forEach(p => {
        const cat = p.manufacturing_category || p.category || project?.project_info?.name || 'Processes';
        cats[cat] = true;
      });
      setExpandedCategories(cats);
    }
  }, [project?.project_info?.name]);

  const toggleCategory = (cat) => {
    setExpandedCategories(prev => ({ ...prev, [cat]: !prev[cat] }));
  };

  // Category display helpers
  const getCategoryColor = (category) => {
    const c = category.toLowerCase();
    if (c === 'cathode') return '#e74c3c';
    if (c === 'anode') return '#3498db';
    if (c === 'separator') return '#f39c12';
    if (c === 'electrolyte') return '#9b59b6';
    if (c === 'assembly') return '#27ae60';
    if (c === 'preparation') return '#28a745';
    if (c === 'disassembly') return '#ffc107';
    if (c === 'chemical_processing') return '#17a2b8';
    if (c === 'thermal_processing') return '#dc3545';
    if (c === 'coating') return '#8e44ad';
    if (c === 'electrode') return '#e65100';
    return '#6c757d';
  };

  const formatCategoryName = (category) => {
    return category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Sort categories
  const manufacturingOrder = ['cathode', 'anode', 'separator', 'electrolyte', 'assembly'];
  const recyclingOrder = ['preparation', 'disassembly', 'chemical_processing', 'thermal_processing'];
  const isManufacturing = project?.project_info?.category === 'Manufacturing';
  const categoryOrder = isManufacturing ? manufacturingOrder : recyclingOrder;

  // Group and sort
  const processCategories = {};
  if (project && project.processes) {
    Object.values(project.processes).forEach(process => {
      const fallbackName = project?.project_info?.name || 'Processes';
      const category = isManufacturing
        ? (process.manufacturing_category || process.category || fallbackName)
        : (process.category || fallbackName);
      if (!processCategories[category]) processCategories[category] = [];
      processCategories[category].push(process);
    });
    Object.keys(processCategories).forEach(cat => {
      processCategories[cat].sort((a, b) => (a.id || '').localeCompare(b.id || ''));
    });
  }

  const sortedCategories = Object.entries(processCategories).sort((a, b) => {
    const iA = categoryOrder.indexOf(a[0].toLowerCase());
    const iB = categoryOrder.indexOf(b[0].toLowerCase());
    if (iA !== -1 && iB !== -1) return iA - iB;
    if (iA !== -1) return -1;
    if (iB !== -1) return 1;
    return a[0].localeCompare(b[0]);
  });

  return (
    <div className="process-tree">
      {/* Explorer Section Header */}
      <div
        className="explorer-section-header"
        onClick={() => setProcessTreeExpanded(!processTreeExpanded)}
      >
        <span className="expand-icon">{processTreeExpanded ? '▼' : '▶'}</span>
        <span className="explorer-section-title">Process Tree</span>
      </div>

      {processTreeExpanded && (
        <div className="explorer-section-body">
          {sortedCategories.length === 0 ? (
            <div className="explorer-empty">No processes loaded</div>
          ) : (
            sortedCategories.map(([category, processes]) => (
              <div key={category} className="explorer-group">
                <div
                  className="explorer-group-header"
                  onClick={() => toggleCategory(category)}
                  style={{ borderLeftColor: getCategoryColor(category) }}
                >
                  <span className="expand-icon">
                    {expandedCategories[category] ? '▼' : '▶'}
                  </span>
                  <span className="explorer-group-name">{formatCategoryName(category)}</span>
                </div>

                {expandedCategories[category] && (
                  <div className="explorer-group-items">
                    {processes.map(process => (
                      <div
                        key={process.id || process.process_id}
                        className={`explorer-item ${selectedProcess?.id === process.id ? 'selected' : ''}`}
                        onClick={() => onSelectProcess(process)}
                        title={process.description || ''}
                      >
                        <span className="explorer-item-id">{process.id || process.process_id}</span>
                        <span className="explorer-item-name">
                          {(process.name || process.process_name || 'Unnamed').replace(/_/g, ' ')}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default ProcessTree;